<template>
    <div class="content w-full flex flex-col justify-center pb-10">
        <Dialog v-model:visible="sharerData.visible" modal :closable="true" :draggable="false"
            :header="translate('share this course', 'ucfirst')" :style="{ width: '35rem' }" class="ml-2 me-2">
            <SharerComponent :link="sharerData.link" :title="sharerData.title" :description="sharerData.description" :socialMediasSharing="['whatsapp']" />
        </Dialog>

        <Dialog v-model:visible="showResoursesDialog" modal :header="translate('resourses', 'ucfirst')" :draggable="false"
            :style="{ width: '40rem' }">
            <div class="my-5">
                <LoaderComponent v-if="loadingResourse" />
                <div v-if="!loadingResourse" class="flex flex-wrap gap-3">
                    <p v-if="resoursesToShow.length < 1">{{ translate('no support material', 'ucfirst') }}</p>
                    <template v-for="(videoResourse, index) of resoursesToShow" :key="index">
                        <Button 
                            :label="videoResourse.title"
                            class="no-bg-button"
                            :icon="imageHandler.getFileIcon(videoResourse.getExtension())"
                            @click="getResourseFile(videoResourse.id, `${videoResourse.title}.${videoResourse.file_path.split('.')[1]}`)"
                            v-tooltip.top="translate('download file', 'ucfirst')"
                        />
                    </template>
                </div>
            </div>
        </Dialog>
        <Dialog v-model:visible="showLessonDialog" modal :header="' '" :draggable="false" @hide="cleanSelectedLesson()" :style="{ width: '50rem' }">
            <div class="my-2">
                <LoaderComponent v-if="loadingLesson" />
                <div v-if="!loadingLesson">
                    <p v-if="lessonToShow == null || !lessonToShow.answer || lessonToShow.answer.length < 1" class="text-2xl">
                        {{ translate('no answer avaliables', 'ucfirst') }}
                    </p>
                    <div v-else>
                        <p class="mt-2 text-xl text-blog-beige border border-blog-red bg-blog-red rounded mb-2">
                            {{ ucfirst(lessonToShow.question) }}
                        </p>
                        <p class="text-lg text-blog-red">
                            {{ ucfirst(lessonToShow.description) }}
                        </p>

                        <Divider />

                        <div class="mt-5">
                            <div v-if="lessonToShow.multipleChoices">
                                <p class="">
                                    {{ translate('choose one of the options below') }}:
                                </p>
                                <div v-for="(answer, index) of lessonToShow.answer" :key="index">
                                    <div class="flex w-full">
                                        <p class="my-auto mr-2">
                                            {{ answer.index }}
                                        </p>
                                        <div :class="getAnswerClass(answer)"
                                            class="p-2 my-2 cursor-pointer border border-2 rounded w-full"
                                            @click.prevent="selectMultipleChoiceAnswer(answer)">
                                            {{ answer.answer }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="!lessonToShow.multipleChoices && !feedbackAnswer">
                                <p class="">{{ translate('write your answer below', 'ucfirst') }}:</p>
                                <Textarea v-model="openAnswer" rows="5" cols="30" />
                            </div>

                            <div v-if="feedbackAnswer" class="mt-5 pt-3 border-t border-iron">
                                <p class="text-lg pb-2">
                                    {{ translate('result', 'ucfirst') }}:
                                    <span class="bold text-lg"
                                        :class="feedbackAnswer.correct ? 'text-green-400' : 'text-red-600'">
                                        <i class="pi" :class="feedbackAnswer.correct ? 'pi-check' : 'pi-times'"></i>
                                    </span>
                                </p>
                                <p class="text-lg pb-2" v-if="!feedbackAnswer.correct">
                                    {{ translate('answer', 'ucfirst') }}:
                                    <span class="bold text-base text-lizard font-semibold text-lg">
                                        {{ getCorrectAnswer() }}
                                    </span>
                                </p>
                                <p class="text-lg pb-2" v-if="feedbackAnswer.justification">
                                    {{ translate('justification', 'ucfirst') }}:
                                    <span class="bold text-base">
                                        {{ feedbackAnswer.justification }}
                                    </span>
                                </p>
                            </div>

                            <Button v-if="canAnswerLesson && !answeredQuestion" :label="translate('send answer', 'ucfirst')"
                                class="no-bg-button mt-4" icon="pi pi-send" :loading="submittingAnswer"
                                @click.prevent="submitAnswer()" />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
        <div class="w-full bg-white">
            <div class="flex justify-between bg-slate-950 mb-5 py-3 px-1 text-iron" v-if="!course.loading">
                <div class="w-full md:w-2/3 md:ml-3 my-auto text-center md:text-left text-lg">
                    {{ course.data.title }}
                </div>
                <div class="hidden md:flex w-1/3 justify-end py-auto">
                    <div class="my-auto">
                        <div @click="showConfirmPopup($event)" class="mr-3 cursor-pointer flex">
                            <div class="circle-container mr-3">
                                <svg class="progress-ring" width="100" height="100">
                                    <circle class="progress-ring__circle" stroke="var(--orange)" stroke-width="4"
                                        fill="transparent" r="15" cx="17.5" cy="17.5"
                                        :style="calculateCourseCompletionPercentage()" />
                                </svg>
                                <div class="icon">
                                    <i class="pi pi-graduation-cap"></i>
                                </div>
                            </div>
                            <div class="my-auto">
                                <span class="hover:opacity-75">
                                    {{ isCourseCompleted().status ? translate('get course certificate', 'ucfirst') : translate('your progress', 'ucfirst') }}
                                </span>
                                <i class="pi pi-sort-down"></i>
                            </div>
                        </div>
                        <div v-if="showConfirmationPopup" class="popup-toast" :class="popupClass" :style="popupStyle">
                            <div class="flex">
                                <div
                                    class="flex flex-col align-items-center w-full gap-3 border-bottom-1 surface-border p-3 mb-3 pb-0">
                                    <p>
                                        {{ courseVideoCompletionData.watched }} de {{ courseVideoCompletionData.total }}
                                        {{ translate('completed') }}.
                                    </p>
                                    <p v-if="!isCourseCompleted().status">
                                        {{ translate('complete the course to obtain your certificate', 'ucfirst') }}.
                                    </p>
                                    <Button v-else
                                        :label="translate('get certificate', 'ucfirst')"
                                        class="bg-button"
                                        @click="generateCourseCertificate()"
                                    />

                                </div>
                                <div class="text-right">
                                    <i class="pi pi-times text-6xl text-primary-500 cursor-pointer"
                                        @click="showConfirmationPopup = false;"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Button class="no-bg-button mr-3" iconPos="right" icon="pi pi-share-alt" label="Compartilhar" @click="openSharer()" />
                </div>
            </div>
            <div class="flex flex-wrap md:flex-nowrap bg-white">
                <div class="w-full md:w-4/5 flex flex-col">
                    <div v-if="selectedVideoId && availableQualities.length > 0" class="flex justify-end mb-2 gap-2 items-center mx-1">
                        <label for="qualitySelect" class="text-slate-800 text-sm font-semibold">Qualidade:</label>
                        <select 
                            id="qualitySelect" 
                            v-model="selectedQuality" 
                            @change="changeQuality"
                            class="bg-zinc-800 text-white border border-iron rounded px-2 py-1 text-sm focus:outline-none cursor-pointer"
                        >
                            <option :value="-1">{{ translate('automatic', 'ucfirst') }}</option>
                            <option 
                                v-for="q in availableQualities" 
                                :key="q.index" 
                                :value="q.index"
                            >
                                {{ q.label }}
                            </option>
                        </select>
                    </div>

                    <div class="video-container bg-iron rounded mb-10 mx-1" :class="!selectedVideoId ? 'flex items-center justify-center' : ''">
                        <video ref="videoPlayer" controls preload="auto" v-if="selectedVideoId"
                            @ended="saveUserVideoStatus(videoStatus.COMPLETE)" :style="getVideoStyle()" />
                        <div class="text-lg" v-else>
                            {{ translate('no video selected', 'capitalize') }}!
                        </div>
                    </div>
                    <LoaderComponent v-if="course.loading" />
                    <section v-if="!course.loading" id="course-info-section" class="md:mx-10 my-5">
                        <CourseDescriptionComponent 
                            :course-data="course.data"
                            :category-data="course.categories"
                            :statistics="course.statistics"
                            :instructor="instructor"
                            :user-lang="store.getters.getUserLang"
                        />
                    </section>
                </div>
                <div
                    class="modules_data w-full md:w-1/5 md:ml-1 overflow-y-auto md:h-100 overflow-y-auto md:h-screen mt-10 md:mt-0 bg-white px-1">
                    <template v-if="!course.loadingModules">
                        <Accordion expandIcon="pi pi-plus" collapseIcon="pi pi-minus" :value="0"
                            class="border border-iron">
                            <AccordionPanel v-for="(moduleObj, index) of course.modules" :key="index" :value="index"
                                class="w-full rounded-none border">
                                <AccordionHeader>
                                    <span class="flex items-center gap-2 w-full">
                                        <span class="font-bold whitespace-wrap">{{ moduleObj.title }}</span>
                                    </span>
                                </AccordionHeader>
                                <AccordionContent>
                                    <div v-for="(video, index) of moduleObj.getVideos()" class="m-0 my-4 flex" :key="index">
                                        <div class="cursor-pointer w-full p-2" v-if="video.active">
                                            <p class="pb-2 flex hover:bg-iron rounded pl-1"
                                                @click.prevent="playVideo(video.id)">
                                                <i v-if="video.alreadWatched()"
                                                    class="pi pi-check-square mt-1 mr-1"></i>
                                                {{ video.description }}
                                            </p>
                                            <div class="flex flex-col w-full border-b border-iron">
                                                <div class="my-auto mr-2 pl-1">
                                                    <small><i class="pi pi-clock mr-1"></i> {{ video.timeInMinutes
                                                        }}m</small>
                                                </div>
                                                <template v-if="video.hasResourses()">
                                                    <Button type="button" icon="pi pi-download"
                                                        @click="openResoursesPopup(video.id)" aria-haspopup="true"
                                                        aria-controls="overlay_menu"
                                                        class="my-3 ml-auto ml-2 no-bg-button" label="Recurso"
                                                        iconPos="right" />
                                                </template>
                                                <template v-if="video.hasLesson()">
                                                    <Button 
                                                        type="button"
                                                        @click="openLessonPopup(video)" 
                                                        aria-haspopup="true"
                                                        aria-controls="overlay_menu"
                                                        class="my-3 ml-auto ml-2"
                                                        :class="isLessonAnswered(video) ? 'bg-button' : 'no-bg-button'"
                                                        :icon="isLessonAnswered(video) ? 'pi pi-check-square' : 'pi pi-pencil'"
                                                        :label="translate('lesson', 'ucfirst')"
                                                        iconPos="right" />
                                                </template>
                                            </div>
                                        </div>

                                    </div>
                                </AccordionContent>
                            </AccordionPanel>
                        </Accordion>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import imageHandler from '@/composables/handlers/ImageHandler';
import { showToast } from '@/composables/toast';
import store from '@/store';
import { useToast } from 'primevue/usetoast';
import { onBeforeUnmount, onMounted, reactive, ref, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
import { ucfirst } from '@/composables/textUtils';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import utils from '@/composables/utils';
import Button from 'primevue/button';
import cookiesHandler from '@/composables/cookiesHandler';
import courseService from '@/services/CourseService';
import CourseVideoService from '@/services/CourseVideoService';
import CourseVideoResourseService from '@/services/CourseVideoResourseService';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import CourseVideoLessonService from '@/services/CourseVideoLessonAnswerService';
import UserGradeService from '@/services/UserGradeService';
import { env } from '@/composables/config';
import SharerComponent from '@/components/misc/SharerComponent.vue';
import dashjs from 'dashjs';
import { minutesToTime } from '@/composables/locales';
import { translate } from '@/composables/translate';
import urlHandler from '@/composables/urlHandler';
import InstructorService from '@/services/InstructorService';
import CourseDescriptionComponent from '@/components/Course/CourseDescriptionComponent.vue';
import CategoryService from '@/services/CategoryService';
import { Divider } from 'primevue';
import { useSessionMessageStore } from '@/stores/sessionMessage';
import CourseService from '@/services/CourseService';

const sessionMessage = useSessionMessageStore();
const toast = useToast();
const route = useRoute();
const videoPlayer = ref(null);
const player = ref(null);

const availableQualities = ref([]);
const selectedQuality = ref(-1);

watch(selectedQuality, (val) => {
    selectedQuality.value = val === -1 ? -1 : parseInt(val, 10);
});

const isInitializingPlayer = ref(false);

let showConfirmationPopup = ref(false);
let popupClass = ref('');
let popupStyle = ref('');
function showConfirmPopup() {
    popupStyle.value = { marginRight: '2rem', marginLeft: '-2rem', maxWidth: '30rem!important', minWidth: '20rem!important' };
    popupClass.value = 'p-3 pb-1 border rounded';
    showConfirmationPopup.value = !showConfirmationPopup.value;
}

const completionPercentage = ref(0);
const courseVideoCompletionData = reactive({
    total: 0,
    watched: 0
});
function updateCourseCompletionPercentage(userCourseVideoStatusCollection = null) {
    if (!userCourseVideoStatusCollection)
        completionPercentage.value = 0;

    const total = userCourseVideoStatusCollection.size();
    let watched = 0;
    for (let item of userCourseVideoStatusCollection.items()) {
        if (item.getValue().isWatched())
            watched++;
    }

    courseVideoCompletionData.total = total;
    courseVideoCompletionData.watched = watched;
    completionPercentage.value = (100 / total) * watched;
}

function calculateCourseCompletionPercentage() {
    const radius = 15;
    const circumference = 2 * Math.PI * radius;

    const lessons = getLessonStatus();

    let totalToDivide = courseVideoCompletionData.total + lessons.total;
    let realized = courseVideoCompletionData.watched + lessons.answered;

    if (totalToDivide === 0) return { strokeDasharray: `${circumference} ${circumference}`, strokeDashoffset: circumference };

    const done = (100 / totalToDivide) * realized;
    const offset = circumference - (done / 100) * circumference;

    return {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: offset,
    };
}

onMounted(() => {
    getInstructor();
    getCourseInfo();
    getUserGrades();
});

onBeforeUnmount(() => {
    destroyPlayer();
});

const instructor = ref(null);
async function getInstructor() {
    instructor.value = await InstructorService.getDefaultInstructor();
}

let course = reactive({
    data: {},
    loading: true,
    modules: [],
    categories: [],
    loadingModules: true,
    statistics: {
        resoursesCount: 0,
        videosNumber: 0,
        lessonsCount: 0,
        reviewsCount: 0
    }
});
async function getCourseInfo() {
    course.loading = true;
    if ([null, '', undefined].includes(route.params) || [null, '', undefined].includes(route.params.uuid))
        return goToDashboard();

    if (!await CourseService.isMyCourse(route.params.uuid)) {
        return goToDashboard();
    }

    let courseCachedData = store.getters.getOnDataChest(`course${route.params.uuid}`);
    let courseObject = {};
    if (!utils.isEmpty(courseCachedData)) {
        courseObject = courseCachedData;
    } else {
        const response = await courseService.showCourse(route.params.uuid);
        if (!response.success())
            return goToDashboard();
        courseObject = response.getContent();
    }
    course.data = courseObject;
    course.categories = await CategoryService.getByCourse(courseObject.uuid);
    course.loading = false;
    getCouseModules();

    const courseStatistics = await courseService.getStatistics(route.params.uuid, true);
    if (courseStatistics.success()) {
        course.statistics = courseStatistics.getContent().getStatisticData();
    }
}

const videoStatus = {
    IMCOMPLETE: 0,
    COMPLETE: 1
};

let videoLoading = ref(false);
let selectedVideoId = ref(null);

// Não usa mais @play no template — evita o loop de evento → fetch → play → evento.
function playVideo(videoId = null) {
    if (videoId == null) {
        showToast(toast, translate('no video found', 'ucfirst'), 'error');
        return;
    }

    if (videoId === selectedVideoId.value && player.value) {
        return;
    }

    selectedVideoId.value = videoId;
    saveUserVideoStatus(videoStatus.IMCOMPLETE);
    fetchAndPlay();
}

async function fetchAndPlay() {
    if (isInitializingPlayer.value || selectedVideoId.value == null) {
        return;
    }

    isInitializingPlayer.value = true;
    videoLoading.value = true;

    const response = await CourseVideoService.getVideoSource(selectedVideoId.value);
    videoLoading.value = false;

    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        isInitializingPlayer.value = false;
        return;
    }

    destroyPlayer();
    await nextTick();

    if (videoPlayer.value) {
        videoPlayer.value.muted = false;
        videoPlayer.value.volume = 0.5;
        player.value = dashjs.MediaPlayer().create();

        player.value.updateSettings({
            streaming: {
                retryAttempts: { MPD: 5 },
                abandonLoadTimeout: 10000,
                buffer: { fastSwitchEnabled: true, stableBufferTime: 30 }
            }
        });

        player.value.extend("RequestModifier", () => ({
            modifyRequestHeader: (xhr) => {
                xhr.setRequestHeader("Authorization", `Bearer ${cookiesHandler.get('TM_TOKEN')}`);
                return xhr;
            },
            modifyRequestURL: (url) => url,
        }), true);

        player.value.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, () => {
            mapAvailableQualities();
            isInitializingPlayer.value = false;
        });

        player.value.on(dashjs.MediaPlayer.events.ERROR, () => {
            isInitializingPlayer.value = false;
        });

        player.value.initialize(
            videoPlayer.value,
            CourseVideoService.getVideoFragmentUrl(selectedVideoId.value),
            true
        );
    } else {
        isInitializingPlayer.value = false;
    }
}

function mapAvailableQualities() {
    if (!player.value) return;

    const bitrates = player.value.getBitrateInfoListFor('video');

    if (bitrates && bitrates.length > 0) {
        availableQualities.value = bitrates.map((info) => {
            const idx = parseInt(info.qualityIndex ?? info.index ?? 0, 10);
            const label = info.height ? `${info.height}p` : `${Math.round(info.bitrate / 1000)} kbps`;
            return {
                index: idx,
                label,
                bitrate: info.bitrate
            };
        }).sort((a, b) => b.bitrate - a.bitrate);
    }
}

function changeQuality() {
    if (!player.value) return;

    if (selectedQuality.value === -1) {
        player.value.updateSettings({
            streaming: {
                abr: { autoSwitchBitrate: { video: true } }
            }
        });
        showToast(toast, translate('quality defined to: Automatic', 'ucfirst'), 'info');
        return;
    }

    const qualityIndex = parseInt(selectedQuality.value, 10);

    if (isNaN(qualityIndex)) {
        showToast(toast, translate('invalid quality', 'ucfirst'), 'error');
        return;
    }

    player.value.updateSettings({
        streaming: {
            abr: { autoSwitchBitrate: { video: false } }
        }
    });

    player.value.setQualityFor('video', qualityIndex, true);
}

function destroyPlayer() {
    if (player.value) {
        player.value.off(dashjs.MediaPlayer.events.STREAM_INITIALIZED);
        player.value.off(dashjs.MediaPlayer.events.ERROR);
        try {
            player.value.pause();
            player.value.reset();
            player.value.destroy();
        } catch (e) {
            // ignora erros ao destruir player já em estado inválido
        }
        player.value = null;
    }
    isInitializingPlayer.value = false;
    availableQualities.value = [];
    selectedQuality.value = -1;
}

function getVideoStyle() {
    return {
        height: '600px!important'
    };
}

async function saveUserVideoStatus(status = 0) {
    let courseModuleCachedData = store.getters.getOnDataChest(`user_course_video_${selectedVideoId.value}`);
    if (!utils.isEmpty(courseModuleCachedData)) {
        if (
            (status == videoStatus.IMCOMPLETE && courseModuleCachedData.status == videoStatus.IMCOMPLETE) ||
            (status == videoStatus.IMCOMPLETE && courseModuleCachedData.status == videoStatus.COMPLETE) ||
            (status == videoStatus.COMPLETE && courseModuleCachedData.status == videoStatus.COMPLETE)
        ) {
            return;
        }
    }

    await CourseVideoService.saveUserCourseVideoStatus(selectedVideoId.value, status);
    getUserCourseVideoStatus();
}

async function getCouseModules() {
    course.loadingModules = true;
    course.modules = [];
    let courseModuleCachedData = store.getters.getOnDataChest(`student_course_module_${route.params.uuid}`);
    if (!utils.isEmpty(courseModuleCachedData)) {
        course.modules = courseModuleCachedData;
    } else {
        const response = await courseService.getCourseModules(route.params.uuid);
        if (!response.success())
            return;
        course.modules = response.getContent();
        store.dispatch('saveOnDataChest', {
            key: `student_course_module_${route.params.uuid}`,
            value: response.getContent()
        });
    }
    course.loadingModules = false;

    getUserCourseVideoStatus();
}

let userCourseVideoStatusCollection = reactive({});
async function getUserCourseVideoStatus() {
    if (course.data.uuid == undefined)
        return;
    const response = await CourseVideoService.getUserCourseVideoStatus(course.data.uuid);
    userCourseVideoStatusCollection = response.getContent();
    for (let moduleObj of course.modules) {
        const collection = userCourseVideoStatusCollection.exitsIn('course_module', moduleObj.id);
        if (!collection.empty()) {
            for (let item of collection.items()) {
                let videoObj = moduleObj.getVideoById(item.getValue().course_module_video);
                if (videoObj) {
                    if (item.getValue().isWatched()) {
                        videoObj.setAsWatched();
                    } else {
                        videoObj.setAsNotWatched();
                    }
                }
            }
        }
    }

    updateCourseCompletionPercentage(userCourseVideoStatusCollection);
}

async function goToDashboard() {
    course.loading = false;
    sessionMessage.errorMessage(translate('no course found', 'ucfirst'));
    await urlHandler.redirectAndScroll('/dashboard/');
    return false;
}

let resoursesToShow = ref([]);
let loadingResourse = ref(false);
let showResoursesDialog = ref(false);
async function openResoursesPopup(videoId = null) {
    showResoursesDialog.value = true;
    loadingResourse.value = true;
    resoursesToShow.value = CourseVideoResourseService.getCachedVideoResourse(videoId);
    if (!resoursesToShow.value)
        resoursesToShow.value = await CourseVideoResourseService.getVideoResourse(videoId);

    loadingResourse.value = false;
}

let openAnswer = ref(null);
let lessonToShow = ref(null);
let loadingLesson = ref(false);
let showLessonDialog = ref(false);

let canAnswerLesson = ref(false);
async function openLessonPopup(videoObj = null) {
    showLessonDialog.value = true;
    loadingLesson.value = true;
    const lessonObj = CourseVideoResourseService.extractLessonFromVideo(videoObj);
    loadingLesson.value = false;

    feedbackAnswerData.correctAnswer = lessonObj.getCorrectAnswers();

    let thisUserGradeObj = userGrades.value[lessonObj.id];
    if (
        thisUserGradeObj != undefined &&
        (
            thisUserGradeObj.isCorrect() ||
            !thisUserGradeObj.hasAttempts()
        )
    ) {
        let answer = lessonObj.getAnswerById(thisUserGradeObj.givenAnswer);

        if (answer) {
            feedbackAnswerData.id = answer.id;
            feedbackAnswerData.answer = answer;
            feedbackAnswerData.correct = answer.correct;
        }

        feedbackAnswer.value = answer;
        canAnswerLesson.value = false;

    } else {
        let answer = thisUserGradeObj.hasAnswer() ? lessonObj.getAnswerById(thisUserGradeObj.givenAnswer) : null;

        if (answer) {
            answer.correct = thisUserGradeObj.correct;
            feedbackAnswer.value = answer;
        }

        canAnswerLesson.value = thisUserGradeObj.hasAttempts();
    }

    lessonToShow.value = lessonObj;
    feedbackAnswerType.value = lessonObj;
    answeredQuestion.value = thisUserGradeObj.hasAnswer();
}

function isLessonAnswered(videoObj = null) {
    const lessonObj = CourseVideoResourseService.extractLessonFromVideo(videoObj);

    if (!lessonObj) {
        return false;
    }

    let thisUserGradeObj = userGrades.value[lessonObj.id];
    return thisUserGradeObj && thisUserGradeObj.isAnswered();
}

function cleanSelectedLesson(dontClean = []) {
    if (![null, undefined].includes(lessonToShow) && !dontClean.includes('lessonToShow'))
        lessonToShow.value = null;
    if (![null, undefined].includes(feedbackAnswer) && !dontClean.includes('feedbackAnswer'))
        feedbackAnswer.value = null;
    if (![null, undefined].includes(answeredQuestion) && !dontClean.includes('answeredQuestion'))
        answeredQuestion.value = null;
    if (![null, undefined].includes(selectedAnswer) && !dontClean.includes('selectedAnswer'))
        selectedAnswer.value = null;
}

let selectedAnswer = ref(null);
async function selectMultipleChoiceAnswer(answerObj) {
    selectedAnswer.value = answerObj;
}

let feedbackAnswerData = reactive({
    id: null,
    answer: null,
    correct: null,
    correctAnswer: {}
});
let feedbackAnswer = ref(null);
let feedbackAnswerType = ref(false);

let answeredQuestion = ref(null);
let submittingAnswer = ref(false);
async function submitAnswer() {
    submittingAnswer.value = true;
    const response = await CourseVideoLessonService.checkLessonAnswer(selectedAnswer.value.id, lessonToShow.value.id);
    submittingAnswer.value = false;
    if (!response) {
        showToast(toast, response.getMessage(), 'error');
        cleanSelectedLesson();
        return;
    }
    const lessonAnswerObj = response.getContent();

    if (lessonAnswerObj) {
        feedbackAnswerData.id = lessonAnswerObj.id;
        feedbackAnswerData.answer = lessonAnswerObj;
        feedbackAnswerData.correct = lessonAnswerObj.correct;
    }

    feedbackAnswer.value = lessonAnswerObj;
    feedbackAnswerType.value = lessonAnswerObj.correct;

    answeredQuestion.value = true;

    await getUserGrades();
    userGrades.value = { ...userGrades.value };

    if (lessonAnswerObj.correct)
        canAnswerLesson.value = false;
}

let userGrades = ref([]);
async function getUserGrades() {
    const response = await UserGradeService.getByCourse(route.params.uuid);
    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }

    userGrades.value = response.getContent();
}

async function getResourseFile(videoResourseId = '', fileName = '') {
    const response = await CourseVideoResourseService.fetchDonwloadData(videoResourseId);
    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }
    const url = window.URL.createObjectURL(new Blob([response.getContent()]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
}

const defaultAnswerClass = 'border-iron text-blog-old-red';
function getAnswerClass(answer = null) {
    if (!answer)
        return defaultAnswerClass;
    if (feedbackAnswerData.id != null) {
        let classNames = 'cursor-default ';
        if (answer.id == feedbackAnswerData.id) {
            return classNames += feedbackAnswerData.correct ? 'border-lizard text-lizard' : 'border-candy text-candy';
        } else if (answer.correct && !feedbackAnswerData.correct) {
            return classNames += 'border-lizard text-lizard';
        }
        return 'border-iron text-white bg-iron cursor-default';
    }
    if (selectedAnswer.value != null)
        return answer.id == selectedAnswer.value.id ? 'border-carrot text-carrot cursor-default' : 'border-iron text-iron';
    return defaultAnswerClass;
}

function getCorrectAnswer() {
    const defaultNotFoundAnswer = translate('not specified');

    const size = Object.keys(feedbackAnswerData.correctAnswer);
    if (size.length < 1)
        return defaultNotFoundAnswer;
    let answer = null;
    for (let key of size) {
        answer = feedbackAnswerData.correctAnswer[key];
    }
    if (answer != null)
        return answer.index;
    return defaultNotFoundAnswer;
}

let sharerData = ref({
    visible: false,
    link: '',
    title: '',
    description: '',
    hashtags: ''
});
function openSharer() {
    if ([null, undefined, ''].includes(course.data))
        return;
    let textToShare = `${translate('check this course', 'ucfirst')}: ${course.data.title}! \n\n`;
    textToShare += `${translate('what will you find?', 'ucfirst')}\n`;
    textToShare += `${course.data.description}\n\n`;
    textToShare += `${translate('price', 'ucfirst')}: R$ ${minutesToTime(course.data.price)}\n\n`;
    textToShare += `${translate('share with your friends and come learn together!', 'ucfirst')} \n\n`;

    sharerData.value.link = `${env('APP_BASE_URL')}course/${course.data.uuid}`;
    sharerData.value.title = textToShare;

    sharerData.value.visible = true;
}

function isCourseCompleted() {
    let allVideosWatched = courseVideoCompletionData.watched == courseVideoCompletionData.total;
    const lessons = getLessonStatus();

    return {
        status: allVideosWatched && lessons.completed,
        lessons: lessons,
        videosWatched: allVideosWatched
    };
}

function getLessonStatus() {
    let lessons = {
        total: 0,
        answered: 0,
        completed: false
    }
    for (let userGradeId of Object.keys(userGrades.value)) {
        const userGrade = userGrades.value[userGradeId];

        if (userGrade.isAnswered()) {
            lessons.answered++;
        }

        lessons.total++;
    }

    if (lessons.total == lessons.answered) {
        lessons.completed = true;
    }

    return lessons;
}

function generateCourseCertificate() {
    const courseUuid = route.params?.uuid;

    if (!courseUuid) {
        showToast(toast, translate('course not found', 'ucfirst'), 'error');
        return;
    }

    const data = {
        content: course.data,
        statistics: course.statistics
    }

    if (!courseService.storageCourseData(courseUuid, data)) {
        showToast(toast, translate('a problemn occured, try again later or contact the suport', 'ucfirst'), 'info');
        return;
    }

    urlHandler.redirectAndScroll(`/dashboard/certificate/${courseUuid}`, {type: 'course'});
}

</script>

<style scoped>
.p-accordioncontent-content {
    padding-left: 0px !important;
    padding-right: 0px !important;
}

.video-container {
    height: 600px;
    width: 100%;
    background-color: #f0f0f0;
    overflow: hidden;
    position: relative;
}

.video-container video {
    height: 100%;
    width: 100%;
    object-fit: contain;
    position: absolute;
    top: 0;
    left: 0;
}

.popup-toast {
    background: #ffffff;
    color: #334155;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    position: absolute;
    margin-top: 10px;
    z-index: 1001;
    transform-origin: center top 0px;
    --overlayArrowLeft: 0px;
    font-family: var(--font-family);
    font-feature-settings: var(--font-feature-settings, normal);
    font-size: 1rem;
    font-weight: normal;
}

/* Course progress */
.circle-container {
    position: relative;
    width: 35px;
    height: 35px;
}

.progress-ring {
    height: 35px;
    width: 35px;
    transform: rotate(-90deg);
}

.progress-ring__circle {
    transition: stroke-dashoffset 0.35s;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
}

.icon {
    position: absolute;
    top: 0;
    left: 0;
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.correct-answer {
    background-color: var(--lizard) !important;
}

.incorrect-answer {
    background-color: var(--candy) !important;
}
</style>